import { useState } from 'react';
import './vehicle-selector.scss';

interface Vehicle {
  Id: number;
  Report: {
    Number: string;
    Date: string;
  };
  Type: {
    TypeId: number;
    Description: string;
  };
  Plate: {
    Number: string;
    Source: {
      SourceId: number;
      Description: string;
      InEmirates: boolean;
    };
    Color: {
      PlateColorId: number;
      Description: string;
      Code: string;
    };
  };
}

interface VehicleSelectorProps {
  vehicles: Vehicle[];
  onSelect: (vehicle: Vehicle) => void;
  language?: 'ar' | 'en' | 'ur';
  selectedVehicle?: Vehicle | null;
}

export default function VehicleSelector({ 
  vehicles, 
  onSelect, 
  language = 'ar',
  selectedVehicle = null 
}: VehicleSelectorProps) {
  const [selected, setSelected] = useState<number | null>(
    selectedVehicle ? selectedVehicle.Id : null
  );

  const texts = {
    ar: {
      title: 'اختر المركبة',
      plateNumber: 'رقم اللوحة',
      source: 'المصدر',
      color: 'اللون',
      type: 'النوع',
      reportNumber: 'رقم التقرير',
      select: 'اختيار',
      selected: 'محدد',
    },
    en: {
      title: 'Select Vehicle',
      plateNumber: 'Plate Number',
      source: 'Source',
      color: 'Color',
      type: 'Type',
      reportNumber: 'Report Number',
      select: 'Select',
      selected: 'Selected',
    },
    ur: {
      title: 'گاڑی منتخب کریں',
      plateNumber: 'پلیٹ نمبر',
      source: 'ذریعہ',
      color: 'رنگ',
      type: 'قسم',
      reportNumber: 'رپورٹ نمبر',
      select: 'منتخب کریں',
      selected: 'منتخب شدہ',
    }
  };

  const t = texts[language];

  const handleSelect = (vehicle: Vehicle) => {
    setSelected(vehicle.Id);
    onSelect(vehicle);
  };

  return (
    <div className="vehicle-selector-container">
      <div className="vehicle-selector-header">
        <h3>{t.title}</h3>
        <p className="vehicle-count">
          {vehicles.length} {language === 'ar' ? 'مركبة' : language === 'ur' ? 'گاڑی' : (vehicles.length === 1 ? 'vehicle' : 'vehicles')}
        </p>
      </div>

      <div className="vehicles-grid">
        {vehicles.map((vehicle) => (
          <div
            key={vehicle.Id}
            className={`vehicle-card ${selected === vehicle.Id ? 'selected' : ''}`}
            onClick={() => handleSelect(vehicle)}
          >
            <div className="vehicle-card-header">
              <div className="plate-display">
                <span className="plate-number">{vehicle.Plate.Number}</span>
                <span className="plate-code">{vehicle.Plate.Color.Code}</span>
              </div>
              {selected === vehicle.Id && (
                <span className="selected-badge">
                  <span className="material-symbols-outlined">check_circle</span>
                  {t.selected}
                </span>
              )}
            </div>

            <div className="vehicle-details">
              <div className="detail-row">
                <span className="detail-label">{t.source}:</span>
                <span className="detail-value">{vehicle.Plate.Source.Description}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t.color}:</span>
                <span className="detail-value">{vehicle.Plate.Color.Description}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t.type}:</span>
                <span className="detail-value">{vehicle.Type.Description}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t.reportNumber}:</span>
                <span className="detail-value">{vehicle.Report.Number}</span>
              </div>
            </div>

            <button 
              className={`select-button ${selected === vehicle.Id ? 'selected' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(vehicle);
              }}
            >
              {selected === vehicle.Id ? t.selected : t.select}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

